import { recolor, toRgba } from '../utils/color';
import {
  fittingStringByWidth,
  getArcLineHeight,
  getCanvasFont,
  toRadian
} from '../utils/string.utilts';
import { LayoutBase } from './LayoutBase';

export default class extends LayoutBase {
  /**
   * 文本内容
   *
   * @private
   */
  private text = '';
  /**
   * 带有配色的文本内容
   *
   * @private
   * @type {{
   *     color: string;
   *     text: string;
   *   }[]}
   */
  private textStrings: {
    color: string;
    text: string;
    sourceId?: string;
  }[] = [];

  getSize(): { width: number; height: number } {
    const {
      style,
      w,
      radius,
      letterSpace = 0,
      verticalAlign = 'top'
    } = this.dataItem as IDataItemText;

    // 设置基本属性
    this.setBaseAttrs(this.ctx);
    this.parseTextString();
    const ctx = this.ctx;

    let width = 0;
    let height = 0;
    if (style == 'normal') {
      const text = this.isAutoSize(String(w))
        ? this.text
        : fittingStringByWidth(ctx, this.text, w, letterSpace);
      const {
        fontBoundingBoxAscent,
        fontBoundingBoxDescent,
        width: _textWidth
      } = ctx.measureText(text);
      width = Math.ceil(_textWidth + (text.length - 1) * letterSpace);
      height = fontBoundingBoxAscent + fontBoundingBoxDescent;
    }
    if (style == 'arc') {
      const { fontBoundingBoxAscent, fontBoundingBoxDescent } = ctx.measureText(
        this.text
      );
      const lineHeight = fontBoundingBoxAscent + fontBoundingBoxDescent;
      const _lineHeight = getArcLineHeight(lineHeight, verticalAlign);
      height = width = (radius + _lineHeight) * 2;
    }
    return { width: Math.floor(width), height: Math.floor(height) };
  }

  private setBaseAttrs(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D
  ) {
    const { color, fontSize, fontId, fontWeight } = this
      .dataItem as IDataItemText;
    ctx.textBaseline = 'ideographic';
    ctx.fillStyle = toRgba(color ?? '#ffffff') ?? 'rgba(255,255,255,1)';

    ctx.font = getCanvasFont({ fontId, fontSize, fontWeight });
  }
  private parseTextString() {
    const {
      _string: string = '',
      children = [],
      color: baseColor
    } = this.dataItem as IDataItemText;

    let text = string;

    if (children.length) {
      const sourceVals = children
        .map((item) => item.source)
        .map((key) => {
          const val = this.getDataSourceValue(key).toString();
          return val;
        });
      const matches = [...text.matchAll(/%[.]?([0]?)([\d]?)([d|f|s])/g)];
      matches.forEach((match, index) => {
        const [pattern, prefix, length, format] = match;
        let value = sourceVals[index];
        const _length = Number(length);
        switch (format) {
          case 'd':
            value = Number.parseInt(value).toString();

            if (match[1] && value?.toString().length < _length) {
              value = value.toString().padStart(+length, prefix);
            }
            break;
          case 'f':
            value = Number(value).toFixed(_length);
            break;
          case 's':
            value = value.toString();
            break;
        }
        text = text.replace(pattern, value + '');
      });
    }
    text = text.replaceAll('%%', '%');

    // 处理多颜色
    const textStrings: typeof this.textStrings = [];

    // const regex = /#[0-9a-zA-Z]{6}\s.*?#{1,}(?=#|$)/g;

    const colorModeReg =
      /#([0-9a-zA-Z][0-9a-zA-Z])?[0-9a-zA-Z]{6}\x20.*?#{1,}(?=#|$)/g;
    const isColorMode = colorModeReg.test(text);
    if (isColorMode) {
      colorModeReg.lastIndex = -1;
      const arr = [...(text + '').matchAll(colorModeReg)];
      arr.forEach((item) => {
        const res = item[0];
        if (!res) return;
        const arr = res.split(/(?<=#[0-9a-zA-Z]{6,8})\s/);
        const [color, text] = arr;
        textStrings.push({
          color,
          text: text.replace(/#$/g, '')
        });
      });
    } else {
      textStrings.push({
        color: baseColor,
        text
      });
    }
    this.textStrings = textStrings;
    this.text = textStrings
      .map((item) => item.text.replaceAll('%%', '%'))
      .join('');
  }

  private isAutoSize(w: string) {
    return ['size_content', '1023'].includes(String(w));
  }

  private parseWrapText(data: {
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
    text: string;
    letterSpace: number;
    maxWidth: number;
    // 所需要的行数
    // lineCount: number;
  }) {
    const { ctx, text, letterSpace, maxWidth } = data;
    const res: {
      start: number;
      end: number;
      width?: number;
      text?: string;
    }[] = [];
    // 依次遍历文本，找到合适的
    const len = text.length;
    let i = 0;
    let j = 0;
    while (i <= j && j < len) {
      const _text = text.slice(i, j + 1);
      const _w = ctx.measureText(_text).width + (j - i) * letterSpace;
      // 如果是新行，并且是第一个字符，则强制放进去
      const isFirstChar = j == i;
      if (_w < maxWidth || isFirstChar) {
        // 如果是最后一个字符
        if (j == len - 1) {
          res.push({
            start: i,
            end: j
          });
          break;
        }
        j++;
      } else {
        // 需要另起一行
        // 将上一行信息保留
        // 上一行的终点为j的上一个字符
        res.push({
          start: i,
          end: j - 1
        });
        i = j;
      }
    }
    res.forEach((item) => {
      const { start, end } = item;
      item.text = text.slice(start, end + 1);
      item.width =
        ctx.measureText(item.text).width + (end - start) * letterSpace;
    });
    return res;
  }

  private drawNormalText() {
    const { dataItem, ctx, x, y, _scale = 1 } = this;

    const node = dataItem as IDataItemText;
    const {
      fontSize,
      fontId,
      fontWeight,
      rotation = 0,
      w,
      h,
      align,
      letterSpace = 0,
      longMode = 'dot',
      lineSpace = 0
    } = node;

    const isAutoSize = this.isAutoSize(String(w));

    const text =
      // 自适应宽度或需要换行或裁剪时不处理文本
      isAutoSize || longMode != 'dot'
        ? this.text
        : fittingStringByWidth(ctx, this.text, w, letterSpace);

    // 文本宽度
    const {
      width: _textWidth,
      fontBoundingBoxAscent,
      fontBoundingBoxDescent
    } = ctx.measureText(text);
    const textWidth = Math.ceil(_textWidth + (text.length - 1) * letterSpace);
    // shape宽度
    const shapeWidth = isAutoSize ? textWidth : w;

    // shape高度
    const textHeight = fontBoundingBoxAscent + fontBoundingBoxDescent;
    // 是否裁剪
    const clip = !isAutoSize && longMode == 'clip';
    // 是否换行
    const wrap = !isAutoSize && longMode == 'wrap';
    // const wrapLineCount = wrapText?.length ?? 0;
    // const wrapHeight = wrapLineCount * textHeight;

    // 绘制文本
    const { canvas: offscreenCanvas } = this.getOffscreen({
      width: clip ? w : wrap ? w : shapeWidth ? shapeWidth : 1,
      // 裁切或者换行时需要裁切，非自动布局也需要按照h进行裁切
      height: clip || wrap || !isAutoSize ? h : textHeight,
      init: (_, ctx) => {
        this.setBaseAttrs(ctx);
        ctx.save();
        ctx.textAlign = 'left';
        if (wrap) {
          const wrapText = wrap
            ? this.parseWrapText({
                ctx,
                maxWidth: w,
                text,
                letterSpace
              })
            : undefined;

          // 多行文本绘制
          if (align == 'right') {
            // 设置旋转中心为右上角
            ctx.translate(w, textHeight);
          } else if (align == 'center') {
            ctx.translate(w / 2, textHeight);
          } else {
            ctx.translate(0, textHeight);
          }

          wrapText?.forEach((_wrapText, lineCount) => {
            const { text = '', width = 0, start } = _wrapText;
            // 不同对齐方式的startX不同
            let startX =
              align == 'center' ? -width / 2 : align == 'right' ? -width : 0;
            const startY = lineCount * (textHeight + lineSpace);
            text.split('').forEach((char, index) => {
              const color = this.getTextColor(index + start);
              if (color) ctx.fillStyle = toRgba(color);
              ctx.fillText(char, startX, startY);
              const nextChar = text[index + 1] ?? '';
              const { width: width2 } = ctx.measureText(nextChar);
              const { width: width3 } = ctx.measureText(char + nextChar);

              // 将绘制坐标移动到位
              startX += width3 - width2 + letterSpace;
            });
          });
        } else {
          // 单行文本绘制
          let startX = 0;
          if (align == 'right') {
            // 设置旋转中心为右上角
            ctx.translate(shapeWidth, textHeight);
            startX = -textWidth;
          } else if (align == 'center') {
            ctx.translate(shapeWidth / 2, textHeight);
            startX = -textWidth / 2;
          } else {
            ctx.translate(0, textHeight);
          }
          text.split('').forEach((char, index) => {
            const color = this.getTextColor(index);
            if (color) ctx.fillStyle = toRgba(color);
            ctx.fillText(char, startX, 0);
            const nextChar = text[index + 1] ?? '';
            const { width: width2 } = ctx.measureText(nextChar);
            const { width: width3 } = ctx.measureText(char + nextChar);

            // 将绘制坐标移动到位
            startX += width3 - width2 + letterSpace;
          });
        }

        ctx.restore();
      },
      dirtyParmas: {
        text,
        colors: this.textStrings.map((item) => item.color)?.join(',') ?? '',
        fontId,
        fontWeight,
        fontSize,
        letterSpace,
        align,
        isAutoSize,
        longMode,
        wrap,
        w,
        h,
        lineSpace,
        // 文本不需要使用缓存
        key: Math.random()
      }
    });

    ctx.save();
    // 将画布移到旋转中心
    ctx.translate(x, y);
    let pivotX = 0;
    const pivotY = 0;
    if (align == 'right') {
      // 设置旋转中心为右上角
      pivotX = shapeWidth;
    } else if (align == 'center') {
      pivotX = shapeWidth / 2;
    }
    ctx.translate(pivotX, pivotY);
    ctx.rotate(toRadian(rotation));
    // 应用元素缩放（用于全局缩放或单独缩放图组内文本）
    ctx.scale(_scale, _scale);
    // 旋转+缩放后，将坐标移动到初始位置
    ctx.translate(-pivotX, -pivotY);
    ctx.translate(-x, -y);
    // 动画（内部会将 offscreen 绘制到主画布）
    this.drawAniFrame(offscreenCanvas);
    ctx.restore();

    this.setRuntimeData({
      width: shapeWidth * _scale,
      height: (clip || wrap || !isAutoSize ? h : textHeight) * _scale,
      rotation,
      textWholeWidth: textWidth
    });
  }

  private getTextColor(index: number) {
    const res: { color: string; start: number; end: number }[] = [];
    let lastStart = 0;
    this.textStrings.forEach((item) => {
      const len = item.text.length;
      res.push({
        color: item.color,
        start: lastStart,
        end: len - 1 + lastStart
      });
      lastStart = len - 1 + lastStart + 1;
    });

    return res.find((item) => item.start <= index && item.end >= index)?.color;
  }

  private getArcCharAngle(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    radius: number,
    charOrWidth: string | number
  ) {
    const width =
      typeof charOrWidth == 'number'
        ? charOrWidth
        : ctx.measureText(charOrWidth).width;
    return Math.asin(width / 2 / radius) * 1.7;
  }

  private drawArcAuxiliaryIfNeed(
    ctx: OffscreenCanvasRenderingContext2D,
    data: {
      centerX: number;
      centerY: number;
      radius: number;
      actualRadius: number;
      startAngle: number;
      span: number;
      reverse: boolean;
      color: string;
    }
  ) {
    const { type = 'none', shapeId } = this.renderer.options.arcAuxiliary ?? {};
    if (shapeId != this._layoutId || type == 'none') return;

    const {
      centerX,
      centerY,
      radius,
      actualRadius,
      startAngle,
      span,
      reverse,
      color
    } = data;

    const offset = toRadian(90);

    // 绘制圆
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.setLineDash([8, 6]);
    ctx.globalAlpha = 0.5;
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    // ctx.arc(centerX, centerY, actualRadius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // 绘制半径示意
    if (type == 'radius') {
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      const x1 = centerX + radius;
      const y1 = centerY;
      ctx.setLineDash([3, 4]);
      ctx.strokeStyle = color;
      ctx.lineTo(x1, y1);
      ctx.stroke();
    }

    const _startAngle = startAngle;
    if (type == 'angleStart') {
      // 起始角度-固定线
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.setLineDash([]);
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(centerX, centerY - radius / 2);
      ctx.stroke();

      // 起始角度-圆弧
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.arc(
        centerX,
        centerY,
        radius / 4,
        0 - offset,
        _startAngle - offset,
        reverse
          ? _startAngle > 0
            ? false
            : true
          : _startAngle > 0
          ? false
          : true
      );
      ctx.stroke();
    }
    if (type == 'angleStart' || type == 'span') {
      // 起始角度-起始角度
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      const x2 = centerX + actualRadius * Math.sin(_startAngle);
      const y2 = centerY - actualRadius * Math.cos(_startAngle);
      ctx.setLineDash([3, 4]);
      ctx.strokeStyle = color;
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }

    if (type == 'span') {
      // 绘制span-endAngle
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      const x3 = centerX + radius * Math.sin(_startAngle + span);
      const y3 = centerY - radius * Math.cos(_startAngle + span);
      ctx.setLineDash([3, 4]);
      ctx.lineTo(x3, y3);
      ctx.stroke();

      // 圆弧
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.arc(
        centerX,
        centerY,
        radius / 4,
        _startAngle - offset,
        _startAngle + span - offset,
        !!reverse
      );
      ctx.stroke();
    }
  }

  /**
   * 绘制弧形文本
   * @param text
   */
  private drawArcText() {
    const { ctx, dataItem, text } = this;
    const node = dataItem as IDataItemText;
    if (!ctx) return;
    const {
      radius,
      span,
      startAngle,
      letterSpace,
      align,
      verticalAlign = 'top',
      fontId,
      fontSize,
      fontWeight
    } = node;

    // lineHeight
    const measureText = ctx.measureText(text);
    const lineHeight =
      measureText.fontBoundingBoxAscent + measureText.fontBoundingBoxDescent;
    this.setRuntimeData({ lineHeight });

    const _lineHeight = getArcLineHeight(lineHeight, verticalAlign);
    const width = (radius + _lineHeight) * 2;
    const height = width;

    // 文本圆弧半径（考虑垂直对齐方式后的弧形文本半径）
    const actualRadius =
      radius -
      (verticalAlign == 'center'
        ? lineHeight / 2
        : verticalAlign == 'top'
        ? 0
        : lineHeight);

    // 对齐方式
    const _span = toRadian(span);
    const reverse = _span < 0;
    const startAngle0 = toRadian(startAngle);
    let _startAngle = startAngle0;
    const _endAngle = _startAngle + _span;

    const spaceAngle = this.getArcCharAngle(ctx, actualRadius, letterSpace);

    // 完整字符串的弧度
    const wholeAngle =
      text.split('').reduce((angle, char) => {
        return angle + this.getArcCharAngle(ctx, actualRadius, char);
      }, 0) +
      (text.length - 1) * spaceAngle;

    if (align == 'center') {
      // const char = text[0] ?? '';
      const midAngle = _startAngle + _span / 2;
      if (reverse) {
        _startAngle = midAngle + wholeAngle / 2;
      } else {
        _startAngle = midAngle - wholeAngle / 2;
      }
    } else if (align == 'right') {
      if (reverse) {
        _startAngle = _endAngle + wholeAngle;
      } else {
        _startAngle = _endAngle - wholeAngle;
      }
    }

    const { canvas: offscreenCanvas } = this.getOffscreen({
      width,
      height,
      init: (_, ctx) => {
        this.setBaseAttrs(ctx);

        // 圆心坐标
        // 需要根据文本垂直位置确定圆形
        const centerX = width / 2;
        const centerY = centerX;

        // 绘制辅助线
        this.drawArcAuxiliaryIfNeed(ctx, {
          centerX,
          centerY,
          radius,
          actualRadius,
          startAngle: startAngle0,
          reverse,
          span: _span,
          color: this.getTextColor(0) ?? '#FFF'
        });

        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.textBaseline = 'ideographic';

        let angle = Number(_startAngle);

        text.split('').forEach((char, index) => {
          ctx.save();

          // 设置颜色
          const color = this.getTextColor(index);
          if (color) ctx.fillStyle = toRgba(color);

          // 当前渲染字符信息
          const { width: currCharWidth, fontBoundingBoxAscent } =
            ctx.measureText(char);
          const _currCharAngleHalf =
            this.getArcCharAngle(ctx, actualRadius, char) / 2;

          //旋转坐标系到每个字符应该达到到角度
          const _reverse = reverse ? -1 : 1;
          ctx.rotate(angle + _currCharAngleHalf * _reverse);

          // 移动到下一个文本坐标（文本左下角）
          // 移动之前，坐标在圆心
          ctx.translate(
            (-currCharWidth / 2) * _reverse,
            -actualRadius * Math.cos(_currCharAngleHalf)
          );

          // 如果是反转文本
          if (reverse) {
            ctx.rotate(Math.PI);
          }

          ctx.fillText(char, 0, reverse ? fontBoundingBoxAscent : 0);

          const _offsetAngle = (_currCharAngleHalf * 2 + spaceAngle) * _reverse;
          angle += _offsetAngle;

          ctx.restore();
        });
        ctx.restore();
      },
      dirtyParmas: {
        radius,
        span,
        startAngle,
        letterSpace,
        align,
        verticalAlign,
        fontId,
        fontSize,
        fontWeight,
        text,
        lineHeight,
        colors: this.textStrings.map((item) => item.color)?.join(',') ?? '',
        arcAuxiliary:
          this.renderer.options.arcAuxiliary?.type +
          '_' +
          this.renderer.options.arcAuxiliary?.shapeId
      }
    });
    // 普通绘制
    // ctx.drawImage(offscreenCanvas, x, y);
    // 动画绘制
    this.drawAniFrame(offscreenCanvas);

    this.setRuntimeData({
      width,
      height,
      rotation: 0
    });
  }

  // 处理文本表盘数据
  private handleTextWatchface() {
    const {
      color = '#ffffff',
      string,
      children = []
    } = this.dataItem as IDataItemText;
    // 获取数据源
    const dataSource = children?.[0].source ?? '';
    const value = dataSource ? this.getDataSourceValue(dataSource) : 0;
    const _str = string.split('/0')?.[Number(value)] ?? '';
    (this.dataItem as IDataItemText)._string = `${color} ${_str}`;
  }

  private recolorTextIfNeed() {
    const {
      colorMode,
      themeColor,
      recolorMode = 'only-image'
    } = this.renderer.options.renderTheme;
    const { recolorEnable } = this.dataItem as IDataItemText;
    if (
      colorMode != 'recolor' ||
      !themeColor ||
      recolorMode != 'only-image' ||
      !recolorEnable
    )
      return;

    const runtimeData = this.getRuntimeState() ?? {};
    const width = runtimeData.width ?? 0;
    const height = runtimeData.height ?? 0;
    if (!width || !height) return;

    recolor(this.ctx, { x: this.x, y: this.y, width, height }, themeColor);
  }

  draw() {
    const { style = 'normal' } = this.dataItem as IDataItemText;

    // // 处理文本表盘
    // if (_isTextWatchface) this.handleTextWatchface();

    // 设置基本属性
    this.parseTextString();
    this.setRuntimeData({
      text: this.text,
      textStrings: this.textStrings
    });

    this.setBaseAttrs(this.ctx);

    // 绘制弧形文本
    if (style === 'arc') {
      this.drawArcText();
    } else if (style === 'normal') {
      this.drawNormalText();
    }

    // 文字表盘重着色处理
    this.recolorTextIfNeed();
  }
}
